import { addMonths } from 'date-fns';
import type { Sport } from '@sharpr/types';
import type { SignupSummaryProduct } from '@/services/billing/buildSignupSummary';

/** SoT for "what the user agrees to pay, when, how long" — derived once in checkout-setup,
 *  consumed by disclosure copy (CA ARL §17602(a)) AND Apple Pay's recurring-request shape.
 *  Both render from this type so they can't disagree on the legal terms (FTC/ARL
 *  no-contradictory-disclosures rule enforced structurally). */
export interface SubscriptionTerms {
  /** True if this transaction adds a sub to an existing cohort (vs first-time signup). */
  isAddSport: boolean;

  /** True if there's a free-trial period before the first paid charge. */
  isTrial: boolean;

  /** Amount charged immediately when the user clicks Subscribe (cents).
   *  Zero for trial flows. */
  todayChargeCents: number;

  /** Combined monthly rate across the sports in this transaction. Apple Pay's
   *  single regularBilling slot carries this. */
  monthlyTotalCents: number;

  /** When the first recurring charge fires.
   *  - Trial: trial_end
   *  - Non-trial fresh signup: now (immediate)
   *  - Non-trial add-sport: inherited cohort anchor */
  recurringStartDate: Date;

  /** Sports covered by this transaction (1+ for signup, 1 for add-sport). */
  sports: Sport[];

  /** Sports signed up in the season-tail window: $0 today, born paused, first
   *  charge at next season's resume (date unknown until the admin rolls
   *  sport_seasons — the pre-resume notice email supplies it). */
  tailSports: Sport[];

  /** Combined monthly that the tail sports will bill once their next season
   *  starts. Disclosed in the consent paragraph (amount known; only the date
   *  is not). 0 when no tail sport is in the cart. */
  tailMonthlyCents: number;

  /** Days of trial remaining. 0 if not a trial. */
  trialDays: number;

  /** Pre-sale: the season opener this signup waits for, if after today. With a trial, the
   *  trial runs `trialDays` from here; without one, nothing is charged today and the first
   *  full month is charged here. Either way the first charge is `recurringStartDate`. */
  coverageStartDate?: Date;

  /** Days with no charge before `recurringStartDate`: the trial length, or for pre-sale
   *  the whole wait to the first charge (pre-season + any trial). Computed from the server's
   *  `nowMs` so the wallet sheet never depends on the browser clock. */
  freeDays: number;
}

/** JSON-serializable shape for transport over the wire (Date → unix seconds). */
export interface SubscriptionTermsDTO {
  isAddSport: boolean;
  isTrial: boolean;
  todayChargeCents: number;
  monthlyTotalCents: number;
  recurringStartUnix: number;
  sports: Sport[];
  tailSports: Sport[];
  tailMonthlyCents: number;
  trialDays: number;
  coverageStartUnix?: number;
  freeDays: number;
}

export function serializeTerms(t: SubscriptionTerms): SubscriptionTermsDTO {
  return {
    isAddSport: t.isAddSport,
    isTrial: t.isTrial,
    todayChargeCents: t.todayChargeCents,
    monthlyTotalCents: t.monthlyTotalCents,
    recurringStartUnix: Math.floor(t.recurringStartDate.getTime() / 1000),
    sports: t.sports,
    tailSports: t.tailSports,
    tailMonthlyCents: t.tailMonthlyCents,
    trialDays: t.trialDays,
    freeDays: t.freeDays,
    ...(t.coverageStartDate
      ? { coverageStartUnix: Math.floor(t.coverageStartDate.getTime() / 1000) }
      : {}),
  };
}

export function deserializeTerms(dto: SubscriptionTermsDTO): SubscriptionTerms {
  return {
    isAddSport: dto.isAddSport,
    isTrial: dto.isTrial,
    todayChargeCents: dto.todayChargeCents,
    monthlyTotalCents: dto.monthlyTotalCents,
    recurringStartDate: new Date(dto.recurringStartUnix * 1000),
    sports: dto.sports,
    tailSports: dto.tailSports,
    tailMonthlyCents: dto.tailMonthlyCents,
    trialDays: dto.trialDays,
    freeDays: dto.freeDays,
    ...(dto.coverageStartUnix != null
      ? { coverageStartDate: new Date(dto.coverageStartUnix * 1000) }
      : {}),
  };
}

export interface BuildSubscriptionTermsArgs {
  products: SignupSummaryProduct[];
  trialOffered: boolean;
  trialDays: number;
  /** Trial end unix seconds. When present, drives recurringStartDate. */
  trialEndUnix?: number;
  /** Pre-sale opener unix seconds (`summary.coverage_start`, set only when in the future). */
  coverageStartUnix?: number;
  /** Inherited cohort anchor unix seconds. Used for add-sport non-trial flows
   *  to align recurring start to the cohort's existing cadence. */
  inheritedBillingCycleAnchorUnix?: number;
  isAddSport: boolean;
  /** Wall-clock anchor (ms). Same value the proration math used so terms +
   *  summary agree on "today" across multi-second handler runtime. */
  nowMs: number;
}

export function buildSubscriptionTerms(args: BuildSubscriptionTermsArgs): SubscriptionTerms {
  const {
    products,
    trialOffered,
    trialDays,
    trialEndUnix,
    coverageStartUnix,
    inheritedBillingCycleAnchorUnix,
    isAddSport,
    nowMs,
  } = args;

  if (products.length === 0) {
    throw new Error('[subscriptionTerms] products is empty');
  }

  const sports = products.map((p) => p.sport);

  const summedFirstCharge = products.reduce(
    (sum, p) => sum + (p.first_charge_cents ?? p.monthly_price_cents),
    0,
  );
  const coverageStartDate =
    coverageStartUnix != null ? new Date(coverageStartUnix * 1000) : undefined;
  // Pre-sale without a trial: billing is deferred to the opener — nothing today.
  const deferredToOpener = !trialOffered && coverageStartDate != null;
  const todayChargeCents = trialOffered || deferredToOpener ? 0 : summedFirstCharge;

  let recurringStartDate: Date;
  if (trialOffered && trialEndUnix) {
    recurringStartDate = new Date(trialEndUnix * 1000);
  } else if (deferredToOpener) {
    recurringStartDate = coverageStartDate;
  } else if (inheritedBillingCycleAnchorUnix) {
    recurringStartDate = new Date(inheritedBillingCycleAnchorUnix * 1000);
  } else {
    // Non-trial fresh signup: recurring series begins one cycle later. Strictly future —
    // Apple Pay rejects a non-future regularBilling.recurringPaymentStartDate.
    recurringStartDate = addMonths(new Date(nowMs), 1);
  }

  const monthlyTotalCents = products.reduce((sum, p) => sum + p.monthly_price_cents, 0);

  return {
    isAddSport,
    isTrial: trialOffered,
    todayChargeCents,
    monthlyTotalCents,
    recurringStartDate,
    sports,
    tailSports: products.filter((p) => p.season_tail).map((p) => p.sport),
    tailMonthlyCents: products
      .filter((p) => p.season_tail)
      .reduce((sum, p) => sum + p.monthly_price_cents, 0),
    trialDays: trialOffered ? trialDays : 0,
    freeDays: coverageStartDate
      ? Math.max(1, Math.ceil((recurringStartDate.getTime() - nowMs) / 86_400_000))
      : trialOffered ? trialDays : 0,
    ...(coverageStartDate ? { coverageStartDate } : {}),
  };
}
